import React, { useCallback, useMemo } from "react";
import { Add, Remove, TouchApp } from "@mui/icons-material";
import { Box, Button, Chip, Divider, Typography } from "@mui/material";

import { Container, Header } from "fond/cityPlanner/AreaPanel/SubareaConfigPanel.styles";
import { getScoreChipColors } from "fond/cityPlanner/helper";
import * as turf from "fond/turf";
import { MultiProjectAreaStatistic, SystemsOfMeasurement } from "fond/types";
import { sqMetresToSqKilometers, sqMetresToSqMiles } from "fond/utils/area";
import { useAppSelector } from "fond/utils/hooks";
import { formatInteger, formatNumber } from "fond/utils/number";
import { NonIdealState } from "fond/widgets";

interface SubareaSelectionPopoutProps {
  selectedIds: string[];
  onToggleSelection: (areaId: string, add: boolean) => void;
  areaStatistics?: MultiProjectAreaStatistic[];
  systemOfMeasurement: SystemsOfMeasurement;
  /**
   * IDs of subareas that already have a project created for them.
   * The Add button will be disabled for these areas.
   */
  projectAreaIds?: string[];
}

const SubareaSelectionPopout: React.FC<SubareaSelectionPopoutProps> = ({
  selectedIds,
  onToggleSelection,
  areaStatistics,
  systemOfMeasurement,
  projectAreaIds,
}) => {
  const selectedFeature = useAppSelector((state) => state.project.selectedFeature);
  const featureId = selectedFeature?.featureProperties?.type === "subarea" ? (selectedFeature.featureProperties.id as string | undefined) : undefined;
  const isSelected = useMemo(() => !!featureId && selectedIds.includes(featureId), [featureId, selectedIds]);
  const hasProject = useMemo(() => !!featureId && !!projectAreaIds?.includes(featureId), [featureId, projectAreaIds]);
  const statistic = useMemo(() => areaStatistics?.find(({ Area }) => Area.ID === featureId), [areaStatistics, featureId]);
  const areaValue = useMemo(() => {
    if (!selectedFeature?.feature) return null;
    const sqMeters = turf.area(selectedFeature.feature);
    return systemOfMeasurement === SystemsOfMeasurement.Imperial ? sqMetresToSqMiles(sqMeters) : sqMetresToSqKilometers(sqMeters);
  }, [selectedFeature?.feature, systemOfMeasurement]);

  const handleToggle = useCallback(() => {
    if (featureId) {
      onToggleSelection(featureId, !isSelected);
    }
  }, [featureId, isSelected, onToggleSelection]);

  const buttonLabel = (() => {
    if (hasProject && !isSelected) return "Project already created";
    if (isSelected) return "Remove from selection";
    return "Add to selection";
  })();

  const isImperial = systemOfMeasurement === SystemsOfMeasurement.Imperial;

  return (
    <Container>
      <Header>Select Subareas</Header>
      <Box sx={{ p: 1 }}>
        {selectedFeature && featureId ? (
          <>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              {selectedFeature.featureProperties.name}
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 0.5, mb: 1 }}>
              <ScoreItem score={selectedFeature.featureProperties.score as number | undefined} />
              <InfoItem label="Addresses" value={statistic ? formatInteger(statistic.AddressCounts.AddressCount) : "—"} />
              <InfoItem label={isImperial ? "Area (sq mi)" : "Area (sq km)"} value={areaValue != null ? formatNumber(areaValue, 2) : "—"} />
            </Box>
            <Button
              variant={isSelected ? "outlined" : "contained"}
              fullWidth
              size="small"
              startIcon={isSelected ? <Remove /> : <Add />}
              onClick={handleToggle}
              disabled={hasProject && !isSelected}
            >
              {buttonLabel}
            </Button>
          </>
        ) : (
          <NonIdealState
            icon={<TouchApp color="action" />}
            description="Click a subarea on the map to add or remove it from your selection."
            size="small"
          />
        )}
      </Box>
    </Container>
  );
};

const cellSx = { display: "flex", flexDirection: "column" } as const;
const labelSx = { display: "block", lineHeight: 1.2, mb: 0.5 } as const;
const valueSx = { display: "flex", alignItems: "center", minHeight: 22 } as const;

const ScoreItem: React.FC<{ score?: number }> = ({ score }) => {
  const label = score == null || score === -1 ? "—" : formatNumber(score, 0);
  const chipColors = getScoreChipColors(score ?? -1);
  return (
    <Box sx={cellSx}>
      <Typography variant="caption" color="text.secondary" sx={labelSx}>
        Score
      </Typography>
      <Box sx={valueSx}>
        <Chip
          size="small"
          label={label}
          sx={{
            backgroundColor: chipColors.bg,
            color: chipColors.fg,
            fontWeight: 600,
            fontSize: "0.875rem",
            minWidth: "max-content",
            height: 20,
            px: 0.5,
          }}
        />
      </Box>
    </Box>
  );
};

const InfoItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={cellSx}>
    <Typography variant="caption" color="text.secondary" sx={labelSx}>
      {label}
    </Typography>
    <Box sx={valueSx}>
      <Typography variant="body2" fontWeight={500}>
        {value}
      </Typography>
    </Box>
  </Box>
);

export default SubareaSelectionPopout;
